import type React from 'react';

import { Plane } from 'lucide-react';

import { formatDate } from '@/lib/utils';

import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

import type { LeasingProject, Component } from '../types';

interface Props {
    project: LeasingProject;
    components?: Component[];
    currentDate?: Date;
}

export function AircraftOverviewCard({
    project,
    components = [],
    currentDate = new Date(),
}: Props) {
    // Extract aircraft details from project
    const msn = project?.msn || project?.leasingAircraft?.msn || '-';
    const registration = project?.leasingAircraft?.registration || '-';
    const aircraftType =
        project?.aircraftType || project?.leasingAircraft?.type || '-';

    // Calculate days until delivery
    const calculateDaysUntil = (dateString?: string | null) => {
        if (!dateString) return null;
        const targetDate = new Date(dateString);
        const diffTime = targetDate.getTime() - currentDate.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    const daysUntilDelivery = calculateDaysUntil(project?.deliveryDate);

    // Get aircraft hours and cycles
    const totalHours = project?.aircraft?.tsn || '-';
    const totalCycles = project?.aircraft?.csn || '-';

    // Determine countdown badge styling based on urgency
    const getCountdownStyle = () => {
        if (daysUntilDelivery === null) return null;
        if (daysUntilDelivery < 0) {
            // Overdue
            return 'bg-red-500 text-white border-red-500';
        }
        if (daysUntilDelivery <= 30) {
            // Urgent
            return 'bg-orange-500 text-white border-orange-500';
        }
        if (daysUntilDelivery <= 90) {
            // Soon
            return 'bg-amber-100 text-amber-800 border-amber-200';
        }
        // Comfortable
        return 'bg-emerald-100 text-emerald-800 border-emerald-200';
    };

    return (
        <Card className="w-full overflow-hidden shadow-xs border-muted-foreground/20 p-0">
            <div className="flex flex-col lg:flex-row">
                {/* Left Section: Aircraft Identity */}
                <div className="flex items-center gap-4 p-4 lg:pr-6 lg:border-r border-b lg:border-b-0 bg-muted/30">
                    <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                        <Plane className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <span className="text-lg font-bold tracking-tight">
                                {aircraftType}
                            </span>
                            <Badge
                                variant="outline"
                                className="font-mono text-[10px] font-semibold border-primary/30 bg-primary/5 text-primary px-1.5"
                            >
                                {registration}
                            </Badge>
                            <span className="flex items-center gap-1 text-[11px]">
                                <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                                <span className="font-medium text-emerald-700">
                                    Active
                                </span>
                            </span>
                        </div>
                        <div className="text-xs text-muted-foreground font-mono">
                            MSN {msn}
                        </div>
                    </div>
                </div>

                {/* Middle Section: Key Stats (compact inline) */}
                <div className="flex-1 flex flex-wrap items-center gap-x-6 gap-y-2 p-4 lg:px-6">
                    <StatItem label="Hours" value={totalHours} unit="FH" />
                    <div className="h-8 w-px bg-border hidden sm:block" />
                    <StatItem label="Cycles" value={totalCycles} unit="FC" />
                    <div className="h-8 w-px bg-border hidden sm:block" />
                    <StatItem
                        label="Delivery"
                        value={formatDate(project?.deliveryDate) || '-'}
                    />
                    <div className="h-8 w-px bg-border hidden sm:block" />
                    <StatItem label="Audit" value="Transition" />
                </div>

                {/* Right Section: Countdown Badge (prominent) */}
                {daysUntilDelivery !== null && (
                    <div className="flex items-center justify-center p-4 lg:pl-6 lg:border-l border-t lg:border-t-0 bg-muted/20">
                        <div className="text-center">
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">
                                Countdown
                            </div>
                            <Badge
                                className={`text-sm font-bold px-3 py-1 ${getCountdownStyle()}`}
                            >
                                T{daysUntilDelivery > 0 ? '-' : '+'}
                                {Math.abs(daysUntilDelivery)}d
                            </Badge>
                        </div>
                    </div>
                )}
            </div>
        </Card>
    );
}

function StatItem({
    label,
    value,
    unit,
}: {
    label: string;
    value: React.ReactNode;
    unit?: string;
}) {
    return (
        <div className="flex flex-col gap-0">
            <span className="text-[9px] uppercase tracking-wider text-muted-foreground/70 font-medium leading-none mb-0.5">
                {label}
            </span>
            <span className="text-sm font-bold text-foreground leading-tight">
                {value}
                {unit && (
                    <span className="text-xs font-normal text-muted-foreground ml-0.5">
                        {unit}
                    </span>
                )}
            </span>
        </div>
    );
}
